import { useNavigate, useSearchParams } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { 
  CreditCard, 
  Shield, 
  CheckCircle2, 
  ArrowLeft, 
  Loader2,
  Tag,
  Calculator
} from 'lucide-react';
import { apiRequest } from '@/utils/api-fetch';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [planDetails, setPlanDetails] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [finalPrice, setFinalPrice] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [formData, setFormData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: '',
    billingAddress: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'US'
    }
  });

  useEffect(() => {
    // Parse query parameters using searchParams
    const planId = searchParams.get('planId');
    const planName = searchParams.get('planName');
    const price = searchParams.get('price');
    const originalPrice = searchParams.get('originalPrice');
    const urlCouponCode = searchParams.get('couponCode');
    
    if (planId && planName && price) {
      setPlanDetails({
        planId: parseInt(planId),
        planName,
        price: parseFloat(price),
        originalPrice: parseFloat(originalPrice || price)
      });
      setFinalPrice(parseFloat(price));
      
      if (urlCouponCode) {
        setCouponCode(urlCouponCode);
        setAppliedCoupon({
          code: urlCouponCode,
          discount_type: searchParams.get('discountType') || 'percentage',
          discount_value: parseFloat(searchParams.get('discountValue') || '0')
        });
      }
    } else {
      // Redirect to pricing if no plan selected
      navigate('/pricing');
    }
  }, [navigate, searchParams]);

  const fetchCouponDetails = async (code) => {
    try {
      const result = await apiRequest('coupons/validate', {
        method: 'POST',
        body: JSON.stringify({ code }),
      });
      
      if (result.success) {
        setAppliedCoupon(result.data);
      }
    } catch (error) {
      console.error('Error fetching coupon details:', error);
    }
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;

    try {
      const result = await apiRequest('coupons/validate', {
        method: 'POST',
        body: JSON.stringify({ code: couponCode }),
      });

      if (result.success) {
        setAppliedCoupon(result.data);
        calculateFinalPrice(planDetails.originalPrice, result.data);
      } else {
        alert(result.error || 'Invalid coupon code');
      }
    } catch (error) {
      console.error('Error applying coupon:', error);
      alert('Error applying coupon code');
    }
  };

  const removeCoupon = () => {
    if (!planDetails) return;
    setAppliedCoupon(null);
    setCouponCode('');
    setFinalPrice(planDetails.originalPrice);
  };

  const calculateFinalPrice = (originalPrice, coupon) => {
    if (!coupon) {
      setFinalPrice(originalPrice);
      return;
    }

    let discountedPrice = originalPrice;
    
    switch (coupon.discount_type) {
      case 'percent':
        discountedPrice = originalPrice * (1 - coupon.discount_value / 100);
        break;
      case 'flat':
        discountedPrice = Math.max(0, originalPrice - coupon.discount_value);
        break;
      case 'free_interviews':
        // Keep original price but add free interviews benefit
        discountedPrice = originalPrice;
        break;
    }

    setFinalPrice(discountedPrice);
  };

  const handleInputChange = (field, value, isNested = false, parentField = null) => {
    if (isNested && parentField) {
      setFormData(prev => ({
        ...prev,
        [parentField]: {
          ...prev[parentField],
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const formatCardNumber = (value) => {
    return value.replace(/\s/g, '').replace(/(\d{4})/g, '$1 ').trim();
  };

  const formatExpiryDate = (value) => {
    return value.replace(/\D/g, '').replace(/(\d{2})(\d)/, '$1/$2');
  };

  const handlePayment = async () => {
    if (!planDetails) return;

    setLoading(true);
    try {
      // Prepare payment data
      const paymentData = {
        planId: planDetails.planId,
        amount: finalPrice,
        originalAmount: planDetails.originalPrice,
        paymentMethod,
        couponId: appliedCoupon?.coupon_id,
        billingDetails: formData.billingAddress,
        cardDetails: {
          number: formData.cardNumber.replace(/\s/g, ''),
          expiryDate: formData.expiryDate,
          cvv: formData.cvv,
          name: formData.cardholderName
        }
      };

      const result = await apiRequest('payments/process', {
        method: 'POST',
        body: JSON.stringify(paymentData),
      });

      if (result.success) {
        // Redirect to success page with transaction details
        navigate(`/payment-success?transactionId=${result.data.transactionId}`);
      } else {
        alert(result.error || 'Payment failed. Please try again.');
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('Payment processing failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getDiscountAmount = () => {
    if (!appliedCoupon) return 0;
    return planDetails.originalPrice - finalPrice;
  };

  if (!planDetails) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p>Loading checkout...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="mb-4 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors flex items-center"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Pricing
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Complete Your Purchase</h1>
          <p className="text-gray-600 mt-2">
            Secure checkout for your {planDetails.planName} plan
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Order Summary */}
          <div className="lg:order-2">
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 sticky top-8">
              <div className="pb-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Calculator className="w-5 h-5" />
                  Order Summary
                </h2>
              </div>
              <div className="pt-4 space-y-4">
                {/* Plan Details */}
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-semibold text-gray-900">{planDetails.planName} Plan</h3>
                    <p className="text-sm text-gray-600">Monthly subscription</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">${planDetails.originalPrice.toFixed(2)}</p>
                  </div>
                </div>

                {/* Coupon Section */}
                <div className="border-t pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Coupon Code</label>
                  {appliedCoupon ? (
                    <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-green-700">
                            {appliedCoupon.code} Applied
                          </p>
                          <p className="text-xs text-green-600">
                            {appliedCoupon.description}
                          </p>
                        </div>
                        <button
                          onClick={removeCoupon}
                          className="px-2 py-1 text-sm text-green-700 hover:text-green-800 hover:bg-green-100 rounded transition-colors"
                        >
                          Remove
                        </button>
                      </div>
                      {appliedCoupon.discount_type !== 'free_interviews' && (
                        <div className="flex justify-between mt-2 pt-2 border-t border-green-200">
                          <span className="text-sm text-green-700">Discount:</span>
                          <span className="text-sm font-medium text-green-700">
                            -${getDiscountAmount().toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="mt-2 flex gap-2">
                      <input
                        type="text"
                        placeholder="Enter coupon code"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        className="flex-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                      />
                      <button
                        onClick={applyCoupon}
                        disabled={!couponCode.trim()}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                      >
                        <Tag className="w-4 h-4 mr-2" />
                        Apply
                      </button>
                    </div>
                  )}
                </div>

                <hr className="border-gray-200 my-4" />

                {/* Total */}
                <div className="flex justify-between items-center text-lg font-bold">
                  <span>Total</span>
                  <span>${finalPrice.toFixed(2)}/month</span>
                </div>

                {/* Special Benefits */}
                {appliedCoupon && appliedCoupon.discount_type === 'free_interviews' && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm font-medium text-blue-700">
                      🎉 Bonus: {appliedCoupon.discount_value} Free Interviews
                    </p>
                    <p className="text-xs text-blue-600">
                      You'll receive {appliedCoupon.discount_value} additional interviews at no extra cost
                    </p>
                  </div>
                )}

                {/* Security Badge */}
                <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                  <Shield className="w-4 h-4 text-green-500" />
                  <span>Secure 256-bit SSL encryption</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Form */}
          <div className="lg:order-1">
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
              <div className="pb-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Payment Information
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Your payment information is encrypted and secure
                </p>
              </div>
              <div className="pt-6 space-y-6">
                {/* Payment Method Selection */}
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">Payment Method</label>
                  <div className="mt-2 grid grid-cols-3 gap-3">
                    <button
                      onClick={() => setPaymentMethod('stripe')}
                      className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                        paymentMethod === 'stripe'
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Credit Card
                    </button>
                    <button
                      onClick={() => setPaymentMethod('paypal')}
                      className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                        paymentMethod === 'paypal'
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      PayPal
                    </button>
                    <button
                      onClick={() => setPaymentMethod('razorpay')}
                      className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                        paymentMethod === 'razorpay'
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Razorpay
                    </button>
                  </div>
                </div>

                {/* Credit Card Form */}
                {paymentMethod === 'stripe' && (
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="cardholderName" className="block text-sm font-medium text-gray-700 mb-2">Cardholder Name</label>
                      <input
                        id="cardholderName"
                        type="text"
                        placeholder="John Doe"
                        value={formData.cardholderName}
                        onChange={(e) => handleInputChange('cardholderName', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                      />
                    </div>

                    <div>
                      <label htmlFor="cardNumber" className="block text-sm font-medium text-gray-700 mb-2">Card Number</label>
                      <input
                        id="cardNumber"
                        type="text"
                        placeholder="1234 5678 9012 3456"
                        value={formData.cardNumber}
                        onChange={(e) => handleInputChange('cardNumber', formatCardNumber(e.target.value))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                        maxLength={19}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="expiryDate" className="block text-sm font-medium text-gray-700 mb-2">Expiry Date</label>
                        <input
                          id="expiryDate"
                          type="text"
                          placeholder="MM/YY"
                          value={formData.expiryDate}
                          onChange={(e) => handleInputChange('expiryDate', formatExpiryDate(e.target.value))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                          maxLength={5}
                        />
                      </div>
                      <div>
                        <label htmlFor="cvv" className="block text-sm font-medium text-gray-700 mb-2">CVV</label>
                        <input
                          id="cvv"
                          type="text"
                          placeholder="123"
                          value={formData.cvv}
                          onChange={(e) => handleInputChange('cvv', e.target.value.replace(/\D/g, ''))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                          maxLength={4}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Billing Address */}
                <div className="space-y-4">
                  <h3 className="text-base font-medium">Billing Address</h3>
                  
                  <div>
                    <label htmlFor="street" className="block text-sm font-medium text-gray-700 mb-2">Street Address</label>
                    <input
                      id="street"
                      type="text"
                      placeholder="123 Main St"
                      value={formData.billingAddress.street}
                      onChange={(e) => handleInputChange('street', e.target.value, true, 'billingAddress')}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">City</label>
                      <input
                        id="city"
                        type="text"
                        placeholder="New York"
                        value={formData.billingAddress.city}
                        onChange={(e) => handleInputChange('city', e.target.value, true, 'billingAddress')}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                      />
                    </div>
                    <div>
                      <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">State</label>
                      <input
                        id="state"
                        type="text"
                        placeholder="NY"
                        value={formData.billingAddress.state}
                        onChange={(e) => handleInputChange('state', e.target.value, true, 'billingAddress')}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-2">ZIP Code</label>
                      <input
                        id="zipCode"
                        type="text"
                        placeholder="10001"
                        value={formData.billingAddress.zipCode}
                        onChange={(e) => handleInputChange('zipCode', e.target.value, true, 'billingAddress')}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                      />
                    </div>
                    <div>
                      <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                      <input
                        id="country"
                        type="text"
                        placeholder="United States"
                        value={formData.billingAddress.country}
                        onChange={(e) => handleInputChange('country', e.target.value, true, 'billingAddress')}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Payment Button */}
                <div className="pt-4">
                  <button
                    onClick={handlePayment}
                    disabled={loading || !formData.cardholderName || !formData.cardNumber}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 text-lg font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <CheckCircle2 className="w-5 h-5 mr-2" />
                    )}
                    {loading ? 'Processing...' : `Pay $${finalPrice.toFixed(2)}`}
                  </button>
                  
                  <p className="text-xs text-gray-500 text-center mt-2">
                    By completing this purchase you agree to our Terms of Service
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
